import React, { useContext, useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Input } from '../../../../components/forms';
import { Grid } from '@material-ui/core';
import {
  CheckCircleOutline,
  EmailOutlined,
  LinkOutlined,
  LockOutlined,
  NotesOutlined,
  PersonOutlineOutlined,
  PhoneOutlined,
  SecurityOutlined,
} from '@material-ui/icons';

import UsersAPI from '../../user-api';
import Button from '../../../../components/common/Button';

import SessionSection from './sessionSection';
import PillarsSection from './pillarSection';

import { UserContext } from '../userContext';
import UserAutoSave from '../UserAutoSave';
import Select from '../../../../components/forms/Select';
import { AppContext } from '../../../../app/AppContext';
import RoleChangeDialog from './roleChangeDialog';

const ALLOWED_ROLE_TYPES = ['mentee', 'mentor', 'none'];

const InformationUser = ({
  onRoleChanged,
  setShowAlert,
  setMessageAlert,
}) => {
  //getting user info from context
  const [state, dispatch] = useContext(UserContext);
  const { roles: appRoles, user: authenticatedUser } = useContext(AppContext);
  const [fallbackRoles, setFallbackRoles] = useState([]);
  const [isRoleDialogOpen, setIsRoleDialogOpen] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [savingRole, setSavingRole] = useState(false);
  const [roleError, setRoleError] = useState('');
  const { user, pillars: availablePillars } = state;
  const normalizedRole = user?.role || { name: 'Sin rol', type: 'none' };
  const roleType = normalizedRole.type;
  const roleOptions = useMemo(() => {
    const contextRoles = Array.isArray(appRoles?.roles)
      ? appRoles.roles
      : [];
    const roles = contextRoles.length ? contextRoles : fallbackRoles;

    return roles.filter((role) => ALLOWED_ROLE_TYPES.includes(role.type));
  }, [appRoles, fallbackRoles]);
  const prePillarIds =
    roleType === 'mentor' && user?.prePillars
      ? user.prePillars
          .split(',')
          .map((pillarId) => parseInt(pillarId, 10))
          .filter((pillarId) => !Number.isNaN(pillarId))
      : [];
  const mentorPillarsFromPreRegistry =
    roleType === 'mentor' && prePillarIds.length
      ? availablePillars.filter((pillar) => prePillarIds.includes(pillar.id))
      : [];
  const normalizedMentorPillars =
    roleType === 'mentor' && mentorPillarsFromPreRegistry.length
      ? mentorPillarsFromPreRegistry
      : Array.isArray(user?.pillars)
      ? user.pillars
      : [];
  const normalizedUser = {
    ...user,
    name:
      user?.name ||
      [user?.firstName, user?.lastNames].filter(Boolean).join(' '),
    celphone: user?.celphone || '',
    role: normalizedRole,
    pillars:
      roleType === 'mentor'
        ? normalizedMentorPillars
        : Array.isArray(user?.pillars)
        ? user.pillars
        : [],
    sessions: Array.isArray(user?.sessions) ? user.sessions : [],
  };

  const markUserInfoAsModified = () => {
    const filteredAutosaved = state.autosaved.filter(
      (entry) => Object.keys(entry)[0] !== 'userInfo'
    );

    dispatch({
      type: 'UPDATE_AUTOSAVE_STATUS',
      payload: {
        autosaved: [...filteredAutosaved, { userInfo: true }],
      },
    });
  };

  const addPillar = (pillar) => {
    const selectedPillars = normalizedUser.pillars.some(
      (currentPillar) => currentPillar.id === pillar.id
    )
      ? normalizedUser.pillars.filter(
          (currentPillar) => currentPillar.id !== pillar.id
        )
      : [...normalizedUser.pillars, pillar];

    dispatch({
      type: 'UPDATE_MENTOR_PILLAR',
      payload: { pillars: selectedPillars },
    });
    markUserInfoAsModified();
  };

  const isAdmin = ['manager', 'admin'].includes(roleType);
  const canManagePillars = roleType === 'mentor';
  const canChangeRole =
    authenticatedUser?.role?.type === 'admin' &&
    Number(authenticatedUser?.id) !== Number(normalizedUser.id);

  const openRoleDialog = async () => {
    setSelectedRoleId(String(normalizedRole.id ?? ''));
    setRoleError('');
    setIsRoleDialogOpen(true);

    if (roleOptions.length) {
      return;
    }

    setLoadingRoles(true);
    try {
      const response = await UsersAPI.getRoles();
      const roles = Array.isArray(response?.data?.roles)
        ? response.data.roles
        : [];
      setFallbackRoles(roles);

      if (!roles.some((role) => ALLOWED_ROLE_TYPES.includes(role.type))) {
        setRoleError('No hay roles disponibles para realizar el cambio.');
      }
    } catch (error) {
      setRoleError(
        error?.data?.message || 'No fue posible cargar los roles disponibles.'
      );
    } finally {
      setLoadingRoles(false);
    }
  };

  const closeRoleDialog = () => {
    if (savingRole) {
      return;
    }

    setIsRoleDialogOpen(false);
    setRoleError('');
  };

  const handleRoleChange = async () => {
    if (savingRole) {
      return;
    }

    const selectedRole = roleOptions.find(
      (role) => String(role.id) === selectedRoleId
    );

    if (!selectedRole || String(selectedRole.id) === String(normalizedRole.id)) {
      return;
    }

    setSavingRole(true);
    setRoleError('');

    try {
      const response = await UsersAPI.updateUserRole(
        normalizedUser.id,
        selectedRole.id
      );
      const updatedUser = response?.data?.user || {};
      const updatedRole =
        updatedUser.role && typeof updatedUser.role === 'object'
          ? updatedUser.role
          : selectedRole;

      dispatch({
        type: 'UPDATE_USER',
        payload: {
          user: {
            ...updatedUser,
            role: updatedRole,
          },
        },
      });

      setMessageAlert({
        message:
          response?.data?.message ||
          (selectedRole.type === 'none'
            ? 'El rol fue actualizado. El usuario puede ingresar directamente a la plataforma.'
            : 'El rol fue actualizado. El usuario deberá completar nuevamente su registro.'),
        type: 'success',
      });
      setShowAlert(true);
      setIsRoleDialogOpen(false);

      if (onRoleChanged) {
        onRoleChanged();
      }
    } catch (error) {
      const message =
        error?.data?.message || 'No fue posible cambiar el rol del usuario.';
      setRoleError(message);
      setMessageAlert({ message, type: 'error' });
      setShowAlert(true);
    } finally {
      setSavingRole(false);
    }
  };

  //it doesn't send "values" since
  // values contains all user data, mixing
  //extra and extradata in the initial state
  const handleSubmitMentee = ({
    email,
    status,
    password = null,
    bio = '',
    linkedin = '',
  }) => {
    UsersAPI.updateUser(normalizedUser.id, {
      email,
      status,
      password,
      bio,
      linkedin,
    })
      .then((response) => {
        setMessageAlert({
          message: response.data.message,
          type: 'success',
        });
        setShowAlert(true);
      })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };
  const handleSubmitMentor = ({
    email,
    status,
    pillars = [],
    password = null,
    bio = '',
    linkedin = '',
  }) => {
    const pillarsToSave =
      status === 'rejected' ? [] : pillars.map((pillar) => pillar.id);

    const payload = {
      email,
      status,
      password,
      bio,
      linkedin,
      pillar: pillarsToSave[0] || null,
    };

    Promise.all([
      UsersAPI.updateUser(normalizedUser.id, payload),
      UsersAPI.updateMenteePillars(pillarsToSave, normalizedUser.id),
    ])
      .then((response) => {
        const [userResponse] = response;
        setMessageAlert({
          message: userResponse.data.message,
          type: 'success',
        });
        setShowAlert(true);
      })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };

  const handleSubmitAdmin = ({ email, status, password = null }) => {
    UsersAPI.updateUser(normalizedUser.id, {
      email,
      status,
      password,
    })
      .then((response) => {
        setMessageAlert({
          message: response.data.message,
          type: 'success',
        });
        setShowAlert(true);
    })
      .catch((e) => {
        setMessageAlert({ message: e.data.message, type: 'error' });
        setShowAlert(true);
      });
  };
  const onSubmit = (values) => {
    const { email, name, password, linkedin, status, bio } = values;
    const { pillars } = normalizedUser;

    if (roleType === 'mentee') {
      handleSubmitMentee({
        email,
        status,
        password,
        bio,
        linkedin,
      });
    } else if (roleType === 'mentor') {
      handleSubmitMentor({
        email,
        status,
        pillars,
        password,
        bio,
        linkedin,
      });
    } else {
      handleSubmitAdmin({ email, password, bio, linkedin, status });
    }
  };
  const mentorAdvancedOptions = [
    { id: 'active', value: 'Activo' },
    { id: 'inactive', value: 'Inactivo' },
    { id: 'rejected', value: 'Rechazado' },
    { id: 'deactivated', value: 'Desactivado' },
  ];

  const menteeAdvancedOptions = [
    { id: 'active', value: 'Activo' },
    { id: 'inactive', value: 'Inactivo' },
    { id: 'deactivated', value: 'Desactivado' },
  ];

  return (
    <div className="edit-information-user">
      <div className="edit-information-user__header">
        <span>Gestión de usuario</span>
        <h3>Edita información del usuario</h3>
      </div>
      <Formik
        initialValues={normalizedUser}
        enableReinitialize
        validationSchema={Yup.object({
          name: Yup.string(),
          email: Yup.string().email('Formato de email incorrecto'),
          password: Yup.string(),
          status: Yup.string(),
        })}
        onSubmit={(values) => onSubmit(values)}
      >
        {({ setFieldValue }) => (
          <Form>
            <UserAutoSave context="userInfo" />
            <Grid container>
              {/* column1  */}
              <Grid item xs={12} lg={6}>
                <section className="user-edit-panel">
                  <div className="user-edit-panel__header">
                    <div>
                      <span className="user-edit-panel__eyebrow">
                        Información editable
                      </span>
                      <h4>Datos de la cuenta</h4>
                    </div>
                    <div className="user-edit-panel__header-icon">
                      <PersonOutlineOutlined />
                    </div>
                  </div>

                  <div className="user-edit-panel__status">
                    <div className="user-edit-panel__status-copy">
                      <CheckCircleOutline />
                      <div>
                        <span>Estado de cuenta</span>
                        <strong>Control de acceso</strong>
                      </div>
                    </div>
                    <div className="activeUser">
                      <Select
                        options={[]}
                        label=""
                        advancedOptions={
                          roleType === 'mentee'
                            ? menteeAdvancedOptions
                            : mentorAdvancedOptions
                        }
                        name="status"
                      />
                    </div>
                  </div>

                  <div className="user-edit-panel__section">
                    <div className="user-edit-panel__section-title">
                      <span>Datos básicos</span>
                    </div>
                    <div className="user-edit-panel__field">
                      <div className="user-edit-panel__field-icon">
                        <PersonOutlineOutlined />
                      </div>
                      <Input
                        name="name"
                        disabled
                        placeholder="Nombre"
                        label="Nombre"
                      />
                    </div>
                    <div className="user-edit-panel__field">
                      <div className="user-edit-panel__field-icon">
                        <EmailOutlined />
                      </div>
                      <Input name="email" placeholder="Email" label="Email" />
                    </div>
                    <div className="user-edit-panel__field">
                      <div className="user-edit-panel__field-icon">
                        <PhoneOutlined />
                      </div>
                      <Input
                        name="celphone"
                        type="tel"
                        disabled
                        placeholder="Sin teléfono registrado"
                        label="Teléfono"
                      />
                    </div>
                  </div>

                  <div className="user-edit-panel__section user-edit-panel__section--security">
                    <div className="user-edit-panel__section-title">
                      <span>Seguridad</span>
                    </div>
                    <div className="user-edit-panel__field">
                      <div className="user-edit-panel__field-icon">
                        <LockOutlined />
                      </div>
                      <Input name="password" type="password" label="Contraseña" />
                    </div>
                    <div className="user-edit-panel__password-help">
                      <div className="user-edit-panel__password-icon">
                        <SecurityOutlined />
                      </div>
                      <div>
                        <strong>Políticas para crear contraseña</strong>
                        <ul>
                          <li>De 6 a 8 caracteres.</li>
                          <li>Al menos 2 letras.</li>
                          <li>Al menos 2 números.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>
                {!isAdmin && (
                  <section className="user-edit-panel user-edit-panel--profile">
                    <div className="user-edit-panel__header">
                      <div>
                        <span className="user-edit-panel__eyebrow">
                          Perfil público
                        </span>
                        <h4>Información profesional</h4>
                      </div>
                      <div className="user-edit-panel__header-icon">
                        <NotesOutlined />
                      </div>
                    </div>
                    <div className="user-edit-panel__field">
                      <div className="user-edit-panel__field-icon">
                        <LinkOutlined />
                      </div>
                      <Input
                        name="linkedin"
                        label="Enlace al perfil de Linkedin"
                        type="text"
                        max={80}
                      />
                    </div>
                    <div className="user-edit-panel__field user-edit-panel__field--textarea">
                      <div className="user-edit-panel__field-icon">
                        <NotesOutlined />
                      </div>
                      <Input
                        name="bio"
                        placeholder="Biografía"
                        label="Biografía"
                        type="textarea"
                        max={500}
                      />
                    </div>
                  </section>
                )}
              </Grid>
              <Grid item xs={12} lg={6}>
                <SessionSection
                  user={normalizedUser}
                  onEditRole={canChangeRole ? openRoleDialog : null}
                >
                  {!isAdmin && canManagePillars && (
                    <PillarsSection onClick={addPillar} />
                  )}
                </SessionSection>
              </Grid>
              {/* column2 */}
              {/** Datos cuenta **/}
              <Grid
                item
                xs={12}
                style={{ backgroundColor: 'white' }}
                className="submit"
              >
                <Grid container justify="center">
                  <Grid item xs={12} sm={6} md={4}>
                    <Button type="submit" widthFull>
                      {isAdmin ? 'Editar usuario' : 'Aceptar'}
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
      <RoleChangeDialog
        currentRole={normalizedRole}
        error={roleError}
        loadingRoles={loadingRoles}
        onClose={closeRoleDialog}
        onConfirm={handleRoleChange}
        onRoleChange={(roleId) => {
          setSelectedRoleId(roleId);
          setRoleError('');
        }}
        open={isRoleDialogOpen}
        roles={roleOptions}
        saving={savingRole}
        selectedRoleId={selectedRoleId}
      />
    </div>
  );
};

export default InformationUser;
